import { Divider, Flex, Grid, SimpleGrid } from 'components'
import { lowerCase, startCase } from 'lodash-es'
import { useRef } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import global from 'utils/globals'
import { EventsSectionHeader } from '.'
import { EventRecord, EventStatus } from '../../../utils'
import EventCardRenderer from './EventCardRenderer'

interface Props {
  events: EventRecord[]
  status: EventStatus
  showWorkspace: boolean
  nextSection?: boolean
  mobileView?: boolean
}

export default function EventsSection({
  events,
  status,
  showWorkspace,
  nextSection,
  mobileView,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [displayMode] = useLocalStorage<string>('event-display-mode', 'grid')

  return (
    <Flex direction="column" gap={4} id={lowerCase(status)} ref={containerRef}>
      <EventsSectionHeader label={startCase(status)} count={events.length} />
      {mobileView ? (
        <SimpleGrid columns={1} gap={5} px={5}>
          {events.map(event => (
            <EventCardRenderer
              key={event.id}
              event={event}
              isLive={status === 'live'}
              isUpcoming={status === 'upcoming'}
              showWorkspace={showWorkspace}
            />
          ))}
        </SimpleGrid>
      ) : displayMode === 'grid' ? (
        <EventsGallery events={events} status={status} showWorkspace={showWorkspace} />
      ) : (
        <Flex direction="column" gap={3} align="center">
          {events.map(event => (
            <EventCardRenderer
              key={event.id}
              event={event}
              listView
              isLive={status === 'live'}
              isUpcoming={status === 'upcoming'}
              showWorkspace={showWorkspace}
            />
          ))}
        </Flex>
      )}

      {nextSection && <Divider {...dividerProps} />}
    </Flex>
  )
}

function EventsGallery({ events, status, showWorkspace }: Props) {
  if (!events) return

  return (
    <Grid
      key={status}
      templateColumns={`repeat(auto-fill, minmax(${global.EVENT_CARD_MIN_WIDTH}, 1fr))`}
      gap={5}
    >
      {events.map(event => (
        <EventCardRenderer
          key={event.id}
          event={event}
          isLive={status === 'live'}
          isUpcoming={status === 'upcoming'}
          showWorkspace={showWorkspace}
        />
      ))}
    </Grid>
  )
}

const dividerProps = {
  orientation: 'horizontal' as const,
  variant: 'dashed' as const,
  borderColor: 'rgba(163, 174, 221, 1)',
  mt: 3,
}
